<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useNotification } from "@/composables/models/notification/useNotification";
import NotificationMenuItem from "@/components/models/message/notification/NotificationMenuItem.vue";

const RECENT_NOTIFICATIONS_COUNT = 3;

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const { data: notifications, isPending, resetUnreadNotificationCount } = useNotification();

const recentNotifications = computed(() =>
  (notifications.value ?? []).slice(0, RECENT_NOTIFICATIONS_COUNT)
);
</script>

<template>
  <div
    class="tw:w-96 tw:max-w-[calc(100vw-1rem)] tw:rounded-2xl tw:bg-surface tw:shadow-xl tw:border tw:border-on-surface/5 tw:overflow-hidden"
    data-testid="notification-menu"
  >
    <div
      class="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:!px-5 tw:!py-3 tw:border-b tw:border-on-surface/5"
    >
      <span class="tw:text-lg tw:font-bold tw:text-on-surface">
        {{ t("models.notification.menuTitle") }}
      </span>

      <button
        type="button"
        class="tw:text-sm tw:font-bold tw:!text-secondary tw:hover:!text-secondaryDarker tw:!cursor-pointer"
        data-testid="notification-menu-mark-all-read"
        @click="resetUnreadNotificationCount"
      >
        {{ t("models.notification.markAllAsRead") }}
      </button>
    </div>

    <div
      v-if="isPending"
      class="tw:flex tw:justify-center tw:!py-8"
    >
      <v-progress-circular
        color="primary"
        indeterminate
        size="24"
      />
    </div>

    <div
      v-else-if="recentNotifications.length === 0"
      class="tw:text-center tw:text-sm tw:text-on-surface/50 tw:!py-8"
    >
      {{ t("models.notification.empty") }}
    </div>

    <template v-else>
      <NotificationMenuItem
        v-for="notification in recentNotifications"
        :key="notification.id"
        :notification="notification"
        @navigate="emit('close')"
      />
    </template>

    <router-link
      class="tw:block tw:text-center tw:text-sm tw:font-semibold tw:!text-on-surface tw:!py-3 tw:border-t tw:border-on-surface/5 tw:hover:bg-on-surface/3 tw:!no-underline"
      to="/notificationsCenter"
      @click="emit('close')"
    >
      {{ t("models.notification.viewAll") }}
    </router-link>
  </div>
</template>
